class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.replayFreeShippingAnimation();
      const forceReset = this.pendingReservationReset === true;
      this.pendingReservationReset = false;
      this.syncReservationCountdown({ forceReset });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  replayFreeShippingAnimation() {
    const freeShipping = this.querySelector('.cart-drawer__free-shipping.is-complete');

    if (!freeShipping) return;

    freeShipping.classList.remove('is-complete');
    freeShipping.offsetWidth;

    requestAnimationFrame(() => {
      freeShipping.classList.add('is-complete');
    });
  }

  getCartLineCount() {
    return this.querySelectorAll('.cart-item').length;
  }

  getReservationStorageKey(timerElement) {
    const key = timerElement?.dataset?.reservationKey || this.reservationStorageKey || 'default';
    return `cart-drawer-reservation-${key}`;
  }

  readReservationState(storageKey) {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (!raw) return null;

      if (raw.startsWith('{')) {
        const parsed = JSON.parse(raw);
        if (parsed && Number.isFinite(Number(parsed.expiresAt))) {
          return {
            expiresAt: Number(parsed.expiresAt),
            lineCount: Number.isFinite(Number(parsed.lineCount))
              ? Number(parsed.lineCount)
              : undefined,
          };
        }
        return null;
      }

      // Legacy format: plain timestamp string
      const expiresAt = Number(raw);
      return Number.isFinite(expiresAt) ? { expiresAt, lineCount: undefined } : null;
    } catch (error) {
      return null;
    }
  }

  writeReservationState(storageKey, state) {
    try {
      window.sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          expiresAt: state.expiresAt,
          lineCount: state.lineCount,
        })
      );
    } catch (error) {
      // Keep the countdown working when session storage is unavailable.
    }
  }

  clearReservationStorage(storageKey) {
    const key = storageKey || (this.reservationStorageKey && this.getReservationStorageKey());

    if (key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        // Ignore storage failures.
      }
    }

    this.reservationStorageKey = undefined;
    this.reservationExpiresAt = undefined;
  }

  setReservationMessageExpired(isExpired) {
    const active = this.querySelector('[data-cart-reservation-active]');
    const expired = this.querySelector('[data-cart-reservation-expired]');

    if (active) active.toggleAttribute('hidden', isExpired);
    if (expired) expired.toggleAttribute('hidden', !isExpired);
  }

  /**
   * Sync / (re)start the reservation countdown.
   * Resets only when a new line item (product) is added — not on quantity changes.
   */
  syncReservationCountdown({ forceReset = false } = {}) {
    const timerElement = this.querySelector('[data-cart-reservation-timer]');
    const lineCount = this.getCartLineCount();

    this.stopReservationCountdown();

    if (!timerElement || lineCount === 0 || this.classList.contains('is-empty')) {
      this.clearReservationStorage();
      return;
    }

    const duration = Number(timerElement.dataset.reservationDuration) || 300;
    const storageKey = this.getReservationStorageKey(timerElement);
    this.reservationStorageKey = timerElement.dataset.reservationKey || 'default';

    const state = this.readReservationState(storageKey);
    const storedLineCount = state?.lineCount;
    const lineCountGrew =
      Number.isFinite(storedLineCount) && lineCount > storedLineCount;
    const shouldReset = forceReset || !state || lineCountGrew;

    let expiresAt;

    if (shouldReset) {
      expiresAt = Date.now() + duration * 1000;
    } else {
      expiresAt = state.expiresAt;
    }

    this.reservationExpiresAt = expiresAt;
    this.writeReservationState(storageKey, { expiresAt, lineCount });

    const updateTimer = () => {
      const timer = this.querySelector('[data-cart-reservation-timer]');

      if (!timer) {
        this.stopReservationCountdown();
        return false;
      }

      const remaining = Math.max(0, Math.ceil((this.reservationExpiresAt - Date.now()) / 1000));
      const minutes = Math.floor(remaining / 60);
      const seconds = String(remaining % 60).padStart(2, '0');

      timer.textContent = `${minutes}:${seconds}`;
      timer.setAttribute('aria-label', `${minutes} minutes and ${seconds} seconds remaining`);

      if (remaining === 0) {
        this.setReservationMessageExpired(true);
        this.stopReservationCountdown();
        return false;
      }

      this.setReservationMessageExpired(false);
      return true;
    };

    if (updateTimer()) {
      this.reservationTimerId = window.setInterval(updateTimer, 1000);
    }
  }

  // Back-compat alias used by older call sites / mental model
  startReservationCountdown(options) {
    this.syncReservationCountdown(options?.reset ? { forceReset: true } : options);
  }

  stopReservationCountdown() {
    if (this.reservationTimerId === undefined) return;

    window.clearInterval(this.reservationTimerId);
    this.reservationTimerId = undefined;
  }

  onCartEmptied() {
    this.stopReservationCountdown();
    this.clearReservationStorage();
  }

  onCartContentsUpdated({ forceReset = false } = {}) {
    this.syncReservationCountdown({ forceReset });
  }

  close() {
    // Keep counting while closed so reopen shows the real remaining time.
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    const previousLineCount = this.getCartLineCount();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const nextLineCount = this.getCartLineCount();
    // Reset only when a new product line appears — not when qty of an existing line grows.
    this.pendingReservationReset = nextLineCount > previousLineCount;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
